import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Avatar } from '@/components/atoms/Avatar';
import { Card } from '@/components/molecules/Card';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { InviteDialog } from '@/components/organisms/InviteDialog';
import { useUsers, useCompanies } from '@/hooks/useBackofficeData';
import type { BackofficeUser } from '@/types/backoffice.types';
import { ROUTES } from '@/constants/routes';
import styles from './UserListPage.module.scss';

export function UserListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { users, isLoading } = useUsers();
  const { companies } = useCompanies();
  const [inviteOpen, setInviteOpen] = useState(false);

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const matchesSearch =
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        const userStatus = user.status ?? (user.isActive ? 'active' : 'inactive');
        const matchesStatus = statusFilter === 'all' || statusFilter === userStatus;
        return matchesSearch && matchesRole && matchesStatus;
      }),
    [users, searchQuery, roleFilter, statusFilter]
  );

  const columns: DataTableColumn<BackofficeUser>[] = [
    {
      key: 'user',
      header: 'Usuario',
      accessor: (row) => (
        <div className={styles.userList__userCell}>
          <Avatar src={row.avatar} alt={row.name} fallback={row.name} size="md" />
          <div className={styles.userList__userInfo}>
            <span className={styles.userList__userName}>{row.name}</span>
            <span className={styles.userList__userEmail}>{row.email}</span>
          </div>
        </div>
      ),
      sortable: true,
      width: '35%',
    },
    {
      key: 'role',
      header: 'Role',
      accessor: (row) => {
        const roleLabels = {
          student: 'Estudiante',
          company_admin: 'Admin empresa',
          backoffice: 'Backoffice',
        };
        return <Badge>{roleLabels[row.role]}</Badge>;
      },
      sortable: true,
    },
    {
      key: 'company',
      header: 'Empresa',
      accessor: (row) => row.companyName || '—',
      sortable: true,
    },
    {
      key: 'level',
      header: 'Nivel',
      accessor: (row) => (row.level ? <Badge>{row.level}</Badge> : '—'),
      sortable: true,
    },
    {
      key: 'status',
      header: 'Estado',
      accessor: (row) => {
        const status = row.status ?? (row.isActive ? 'active' : 'inactive');
        const map = {
          active: { label: 'Activo', variant: 'success' as const },
          inactive: { label: 'Inactivo', variant: 'error' as const },
          pending: { label: 'Pendiente', variant: 'warning' as const },
        };
        const s = map[status];
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
      sortable: true,
    },
    {
      key: 'registered',
      header: 'Registro',
      accessor: (row) => new Date(row.registeredAt).toLocaleDateString(),
      sortable: true,
    },
  ];

  const handleRowClick = (user: BackofficeUser) => {
    // Las invitaciones pendientes no son usuarios reales: no tienen detalle.
    if (user.isInvitation) return;
    navigate(`${ROUTES.BACKOFFICE.USERS}/${user.id}`);
  };

  return (
    <div className={styles.userList}>
      <div className={styles.userList__header}>
        <div>
          <h1>Usuarios</h1>
          <p className={styles.userList__subtitle}>Gestiona todos los usuarios de la plataforma</p>
        </div>
        <Button leftIcon={<Plus />} onClick={() => setInviteOpen(true)}>
          Crear usuario
        </Button>
      </div>

      <Card className={styles.userList__filters}>
        <div className={styles.userList__filtersGrid}>
          <Input
            type="search"
            placeholder="Buscar por nombre o email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Select
            placeholder="Todos los roles"
            value={roleFilter}
            onValueChange={setRoleFilter}
            options={[
              { label: 'Todos los roles', value: 'all' },
              { label: 'Estudiante', value: 'student' },
              { label: 'Admin empresa', value: 'company_admin' },
              { label: 'Backoffice', value: 'backoffice' },
            ]}
          />
          <Select
            placeholder="Todos los estados"
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { label: 'Todos los estados', value: 'all' },
              { label: 'Activo', value: 'active' },
              { label: 'Pendiente', value: 'pending' },
              { label: 'Inactivo', value: 'inactive' },
            ]}
          />
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={filteredUsers}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        emptyMessage="No se encontraron usuarios"
      />

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        companies={companies.map((c) => ({ id: c.id, name: c.name }))}
        allowRoleSelection
      />
    </div>
  );
}

export default UserListPage;
